import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft,
  FaBuilding,
  FaUserCircle,
  FaSignOutAlt,
  FaServer,
  FaHistory,
  FaEye,
  FaBriefcase,
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaTimesCircle,
  FaPlay,
  FaPlug,
  FaFilter,
  FaSyncAlt,
  FaListAlt,
  FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const EVENT_ICONS = {
  property_viewed: FaEye,
  job_created: FaBriefcase,
  quote_submitted: FaFileInvoiceDollar,
  quote_approved: FaCheckCircle,
  quote_declined: FaTimesCircle,
  job_started: FaPlay,
  job_completed: FaCheckCircle,
  api_test_received: FaPlug,
  check_in: FaPlay,
  check_out: FaTimesCircle,
  document_uploaded: FaFileInvoiceDollar,
  document_viewed: FaFileInvoiceDollar,
  property_edited: FaBuilding,
  property_created: FaBuilding,
  access_assigned: FaUserCircle,
  access_removed: FaUserCircle
};

const EVENT_LABELS = {
  property_viewed: 'Property viewed',
  job_created: 'Job added',
  quote_submitted: 'Quote submitted',
  quote_approved: 'Quote accepted',
  quote_declined: 'Quote declined',
  job_started: 'Job started',
  job_completed: 'Job completed',
  api_test_received: 'HydraJaws API / test received',
  check_in: 'Check-in',
  check_out: 'Check-out',
  document_uploaded: 'Document uploaded',
  document_viewed: 'Document viewed',
  property_edited: 'Property edited',
  property_created: 'Property created',
  access_assigned: 'Access assigned',
  access_removed: 'Access removed'
};

const CloudStaffBuildingLog = () => {
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [buildings, setBuildings] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [property, setProperty] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
    loadBuildings();
  }, [navigate]);

  useEffect(() => {
    if (selectedPropertyId) loadBuildingLog();
    else {
      setProperty(null);
      setEvents([]);
    }
  }, [selectedPropertyId]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserName(`${data.firstName || data.first_name || ''} ${data.lastName || data.last_name || ''}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadBuildings = async () => {
    try {
      setBuildingsLoading(true);
      const res = await fetch(getApiEndpoint('/api/cloud-staff/buildings'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBuildings(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load buildings');
    } finally {
      setBuildingsLoading(false);
    }
  };

  const loadBuildingLog = async () => {
    if (!selectedPropertyId) return;
    setLoading(true);
    setRefreshing(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/cloud-staff/building-log?propertyId=${selectedPropertyId}&limit=500`),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load building log');
      }
      const data = await res.json();
      setProperty(data.property || null);
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load building log');
      setProperty(null);
      setEvents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const filteredEvents =
    typeFilter === 'all'
      ? events
      : events.filter((e) => e.type === typeFilter);

  const uniqueTypes = [...new Set(events.map((e) => e.type))].sort();

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <Link to="/cloud-staff/dashboard">
            <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          </Link>
          <span className="cs-header-badge">Cloud Staff</span>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <Link to="/cloud-staff/dashboard" className="cs-icon-btn" title="Dashboard">
              <FaServer />
            </Link>
            <button
              type="button"
              className="cs-user-avatar"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="User menu"
            >
              <FaUserCircle />
            </button>
            {showUserMenu && (
              <div className="cs-user-menu">
                <div className="cs-user-menu-header">
                  <FaUserCircle className="cs-menu-avatar" />
                  <span className="cs-menu-name">{userName || 'User'}</span>
                </div>
                <button type="button" className="cs-menu-item" onClick={handleLogout}>
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="building-log" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #21262d'
            }}
          >
            <Link
              to="/cloud-staff/dashboard"
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid #30363d',
                color: '#8b949e',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
            >
              <FaArrowLeft />
            </Link>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}>
                Building Log
              </h1>
              <p style={{ fontSize: '0.85rem', color: '#8b949e', margin: 0 }}>
                In-depth activity for one building: views, jobs, quotes, tests, HydraJaws API
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              flexWrap: 'wrap'
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#8b949e', fontSize: '0.9rem' }}>
              <FaBuilding />
              Select building
            </label>
            <select
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              disabled={buildingsLoading}
              style={{
                minWidth: '220px',
                padding: '0.5rem 0.75rem',
                background: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: '8px',
                color: '#e1e4e8',
                fontSize: '0.9rem'
              }}
            >
              <option value="">Choose a property...</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name || `#${b.id}`}
                </option>
              ))}
            </select>
            {selectedPropertyId && (
              <>
                <button
                  type="button"
                  onClick={loadBuildingLog}
                  disabled={refreshing}
                  className="cs-icon-btn"
                  style={{ padding: '0.5rem 0.75rem' }}
                  title="Refresh"
                >
                  <FaSyncAlt style={refreshing ? { animation: 'spin 0.8s linear infinite' } : {}} /> Refresh
                </button>
                {typeFilter !== 'all' && (
                  <span style={{ fontSize: '0.8rem', color: '#8b949e' }}>
                    Filter: {EVENT_LABELS[typeFilter] || typeFilter}
                  </span>
                )}
              </>
            )}
          </div>

          {selectedPropertyId && property && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid #30363d',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#8b949e'
              }}
            >
              <strong style={{ color: '#e1e4e8' }}>{property.name}</strong>
              {property.address && ` · ${property.address}`}
            </div>
          )}

          {selectedPropertyId && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                <FaFilter style={{ color: '#484f58', fontSize: '0.85rem' }} />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  style={{
                    padding: '0.4rem 0.6rem',
                    background: '#0d1117',
                    border: '1px solid #30363d',
                    borderRadius: '6px',
                    color: '#e1e4e8',
                    fontSize: '0.8rem'
                  }}
                >
                  <option value="all">All activity</option>
                  {uniqueTypes.map((t) => (
                    <option key={t} value={t}>
                      {EVENT_LABELS[t] || t}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: '0.8rem', color: '#8b949e' }}>
                  {filteredEvents.length} entries
                </span>
              </div>

              {loading ? (
                <div className="cs-loading">
                  <div className="cs-spinner" />
                  <p>Loading building log...</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="cs-empty-state">
                  <FaListAlt />
                  <p>No activity for this building yet.</p>
                  <p style={{ fontSize: '0.85rem', color: '#8b949e', marginTop: '0.5rem' }}>
                    Events appear when someone views the building, adds jobs, submits or accepts quotes,
                    completes jobs, or when HydraJaws sends test results. More event types can be added later.
                  </p>
                </div>
              ) : (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    border: '1px solid #30363d',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#0d1117'
                  }}
                >
                  {filteredEvents.map((ev) => {
                    const Icon = EVENT_ICONS[ev.type] || FaHistory;
                    const label = EVENT_LABELS[ev.type] || ev.type;
                    return (
                      <li
                        key={ev.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '1rem',
                          padding: '0.85rem 1rem',
                          borderBottom: '1px solid #21262d',
                          fontSize: '0.9rem'
                        }}
                      >
                        <span
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: ev.source === 'hydrajaws' ? 'rgba(88,166,255,0.15)' : 'rgba(255,255,255,0.06)',
                            color: ev.source === 'hydrajaws' ? '#58a6ff' : '#8b949e',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <Icon />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#e1e4e8', marginBottom: '0.25rem' }}>
                            {ev.description || label}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#8b949e' }}>
                            {ev.user_name && (
                              <span>
                                <FaUserCircle style={{ marginRight: '0.35rem' }} />
                                {ev.user_name}
                              </span>
                            )}
                            {ev.source === 'hydrajaws' && (
                              <span style={{ color: '#58a6ff' }}>HydraJaws API</span>
                            )}
                            <span>{formatDate(ev.timestamp)}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}

          {!selectedPropertyId && !buildingsLoading && (
            <div className="cs-empty-state">
              <FaBuilding />
              <p>Select a building above to view its full activity log.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CloudStaffBuildingLog;
